import React, { useState, useEffect } from "react";
import PublicLayout from "../layouts/PublicLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, CheckCircle, Handshake, Globe, User, Building2, Briefcase, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 5 Partnership Pathways (must match NewPartnerProgramPage.jsx)
const PARTNER_PATHWAYS = [
  {
    name: "Referral & Client Partners",
    short: "Introduce organisations to CNESS and assist them through onboarding.",
  },
  {
    name: "Assessment & Implementation Partners",
    short: "Guide organisations through readiness, gap analyses and improvement projects.",
  },
  {
    name: "Media & Community Partners",
    short: "Amplify certified organisations through media, events and communities.",
  },
  {
    name: "Affiliate & Influencer Partners",
    short: "Drive awareness and discovery via blogs, newsletters and influencer channels.",
  },
  {
    name: "Certification & Bundle Partners",
    short: "Integrate CNESS into ESG software, compliance platforms or training programmes.",
  },
];

const PartnerApplicationPage = () => {
  const [formData, setFormData] = useState({
    applicant_type: "organization",
    organization_name: "",
    website: "",
    organization_type: "",
    organization_size: "",
    industry_focus: "",
    geographic_reach: "",
    contact_name: "",
    contact_title: "",
    email: "",
    phone: "",
    partner_types: [],
    additional_info: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [masterData, setMasterData] = useState({
    industries: [],
    organization_types: [],
    organization_sizes: [],
    geographic_reach: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const response = await axios.get(`${API}/partners/master-data`);
        setMasterData({
          industries: response.data.industries || [],
          organization_types: response.data.organization_types || [],
          organization_sizes: response.data.organization_sizes || [],
          geographic_reach: response.data.geographic_reach || [],
        });
      } catch (error) {
        console.error("Error fetching master data:", error);
        setMasterData({
          industries: ["Technology", "Healthcare", "Finance", "Manufacturing", "Retail", "Education"],
          organization_types: [
            "Private Limited Company",
            "Public Limited Company",
            "LLP / Partnership",
            "Sole Proprietorship",
            "Non-Profit / NGO",
            "Government / Public Sector",
            "Educational Institution",
            "Startup",
            "Cooperative",
            "Trust / Foundation",
          ],
          organization_sizes: [
            "Micro (1-9 employees)",
            "Small (10-49 employees)",
            "Medium (50-249 employees)",
            "Large (250-999 employees)",
            "Enterprise (1000+ employees)",
          ],
          geographic_reach: ["Local", "Regional", "National", "International", "Global"],
        });
      } finally {
        setLoading(false);
      }
    };
    fetchMasterData();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const togglePathway = (name) => {
    setFormData((prev) => ({
      ...prev,
      partner_types: prev.partner_types.includes(name)
        ? prev.partner_types.filter((p) => p !== name)
        : [...prev.partner_types, name],
    }));
  };

  const isOrg = formData.applicant_type === "organization";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (formData.partner_types.length === 0) {
      setSubmitError("Please select at least one partnership pathway.");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        // For individuals, clear org-only fields server-side won't expect (they are optional)
        organization_name: isOrg ? formData.organization_name : "",
        organization_type: isOrg ? formData.organization_type : "",
        organization_size: isOrg ? formData.organization_size : "",
        industry_focus: isOrg ? formData.industry_focus : "",
        geographic_reach: isOrg ? formData.geographic_reach : "",
        website: isOrg ? formData.website : "",
      };
      const response = await axios.post(`${API}/partners/apply`, payload);
      if (response.data.success) {
        setSubmitSuccess(true);
        setFormData({
          applicant_type: "organization",
          organization_name: "",
          website: "",
          organization_type: "",
          organization_size: "",
          industry_focus: "",
          geographic_reach: "",
          contact_name: "",
          contact_title: "",
          email: "",
          phone: "",
          partner_types: [],
          additional_info: "",
        });
        // Scroll to top to reveal the success banner
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      let errorMessage = "There was an error submitting your application. Please try again.";
      const detail = error.response?.data?.detail;
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join(" • ");
      } else if (detail && typeof detail === "object") {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading application form...</p>
        </div>
      </div>
    );
  }

  return (
    <PublicLayout>
      <>
        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="partner-app-hero">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
                <Handshake className="w-4 h-4 text-[#FDB813]" />
                <span className="text-sm font-medium text-[#1E3A5F]">Partner Application</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
                Become a <span className="text-[#FDB813]">CNESS Partner</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Collaborate on programmes, training, and scale-up initiatives aligned to the Six-Pillar Framework.
                Join the ecosystem driving conscious business transformation.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-600">
                <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1.5 text-green-600" /> 5-minute application</span>
                <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1.5 text-green-600" /> Response in 1 business day</span>
                <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1.5 text-green-600" /> No upfront fees</span>
              </div>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="py-16 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] mb-3">Tell us about you</h2>
                <p className="text-gray-600">A friendly form — only the essentials. We'll handle the rest.</p>
              </div>

              {submitSuccess && (
                <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl flex items-center" data-testid="partner-app-success">
                  <CheckCircle className="h-7 w-7 text-green-500 mr-4 flex-shrink-0" />
                  <div>
                    <p className="text-green-700 font-semibold">Application submitted successfully!</p>
                    <p className="text-green-600 text-sm">
                      Thank you for your interest in partnering with CNESS. We've emailed you a confirmation
                      and will get back to you within 1 business day.
                    </p>
                  </div>
                </div>
              )}

              <Card className="shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <form onSubmit={handleSubmit} className="space-y-10">
                    {/* Applicant Type */}
                    <div>
                      <h3 className="text-lg font-bold text-[#1E3A5F] mb-4 flex items-center">
                        <User className="h-5 w-5 text-[#FDB813] mr-2" />
                        Applying as
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: "organization", label: "Organisation", Icon: Building2 },
                          { value: "individual", label: "Individual", Icon: User },
                        ].map((opt) => {
                          const active = formData.applicant_type === opt.value;
                          const Icon = opt.Icon;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, applicant_type: opt.value })}
                              data-testid={`applicant-type-${opt.value}`}
                              className={`p-5 rounded-xl border-2 flex items-center gap-3 transition-all text-left ${
                                active
                                  ? "border-[#FDB813] bg-[#FEF3C7]/40"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                            >
                              <Icon className={`w-5 h-5 ${active ? "text-[#1E3A5F]" : "text-gray-500"}`} />
                              <span className={`font-semibold ${active ? "text-[#1E3A5F]" : "text-gray-700"}`}>
                                {opt.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Organisation Info (only when isOrg) */}
                    {isOrg && (
                      <div>
                        <h3 className="text-lg font-bold text-[#1E3A5F] mb-6 flex items-center">
                          <Globe className="h-5 w-5 text-[#FDB813] mr-2" />
                          Organisation Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <Field label="Organisation Name" required>
                            <input
                              type="text"
                              name="organization_name"
                              value={formData.organization_name}
                              onChange={handleInputChange}
                              className={inputCls}
                              placeholder="Your organisation name"
                              required={isOrg}
                              data-testid="partner-org-name"
                            />
                          </Field>
                          <Field label="Website">
                            <input
                              type="url"
                              name="website"
                              value={formData.website}
                              onChange={handleInputChange}
                              className={inputCls}
                              placeholder="https://yourorganisation.com"
                              data-testid="partner-website"
                            />
                          </Field>
                          <Field label="Organisation Type" required>
                            <select
                              name="organization_type"
                              value={formData.organization_type}
                              onChange={handleInputChange}
                              className={inputCls}
                              required={isOrg}
                              data-testid="partner-org-type"
                            >
                              <option value="">Select organisation type</option>
                              {masterData.organization_types.map((t, i) => <option key={i} value={t}>{t}</option>)}
                            </select>
                          </Field>
                          <Field label="Organisation Size" required>
                            <select
                              name="organization_size"
                              value={formData.organization_size}
                              onChange={handleInputChange}
                              className={inputCls}
                              required={isOrg}
                              data-testid="partner-org-size"
                            >
                              <option value="">Select size</option>
                              {masterData.organization_sizes.map((s, i) => <option key={i} value={s}>{s}</option>)}
                            </select>
                          </Field>
                          <Field label="Industry Focus" required>
                            <select
                              name="industry_focus"
                              value={formData.industry_focus}
                              onChange={handleInputChange}
                              className={inputCls}
                              required={isOrg}
                              data-testid="partner-industry"
                            >
                              <option value="">Select industry</option>
                              {masterData.industries.map((it, i) => <option key={i} value={it}>{it}</option>)}
                            </select>
                          </Field>
                          <Field label="Geographic Reach" required>
                            <select
                              name="geographic_reach"
                              value={formData.geographic_reach}
                              onChange={handleInputChange}
                              className={inputCls}
                              required={isOrg}
                              data-testid="partner-geo"
                            >
                              <option value="">Select geographic reach</option>
                              {masterData.geographic_reach.map((g, i) => <option key={i} value={g}>{g}</option>)}
                            </select>
                          </Field>
                        </div>
                      </div>
                    )}

                    {/* Contact Information */}
                    <div>
                      <h3 className="text-lg font-bold text-[#1E3A5F] mb-6 flex items-center">
                        <User className="h-5 w-5 text-[#FDB813] mr-2" />
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label="Contact Name" required>
                          <input
                            type="text"
                            name="contact_name"
                            value={formData.contact_name}
                            onChange={handleInputChange}
                            className={inputCls}
                            placeholder="Full name"
                            required
                            data-testid="partner-contact-name"
                          />
                        </Field>
                        <Field label="Title / Role">
                          <input
                            type="text"
                            name="contact_title"
                            value={formData.contact_title}
                            onChange={handleInputChange}
                            className={inputCls}
                            placeholder="CEO, Director, Consultant, etc."
                            data-testid="partner-contact-title"
                          />
                        </Field>
                        <Field label="Email" required>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={inputCls}
                            placeholder="you@company.com"
                            required
                            data-testid="partner-email"
                          />
                        </Field>
                        <Field label="Phone">
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={inputCls}
                            placeholder="+1 (555) 123-4567"
                            data-testid="partner-phone"
                          />
                        </Field>
                      </div>
                    </div>

                    {/* Partner Pathways (multi-select) */}
                    <div>
                      <h3 className="text-lg font-bold text-[#1E3A5F] mb-2 flex items-center">
                        <Briefcase className="h-5 w-5 text-[#FDB813] mr-2" />
                        Partnership Pathways
                      </h3>
                      <p className="text-sm text-gray-600 mb-5">
                        Select one or more pathways that best describe how you'd like to collaborate with CNESS.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {PARTNER_PATHWAYS.map((p) => {
                          const checked = formData.partner_types.includes(p.name);
                          return (
                            <label
                              key={p.name}
                              data-testid={`partner-pathway-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                                checked
                                  ? "border-[#FDB813] bg-[#FEF3C7]/30"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePathway(p.name)}
                                className="mt-1 w-4 h-4 text-[#FDB813] border-gray-300 rounded focus:ring-[#FDB813]"
                              />
                              <span>
                                <span className="block font-semibold text-[#1E3A5F]">{p.name}</span>
                                <span className="block text-sm text-gray-600 mt-1">{p.short}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-lg font-bold text-[#1E3A5F] mb-4 flex items-center">
                        <Globe className="h-5 w-5 text-[#FDB813] mr-2" />
                        Additional Information
                      </h3>
                      <Field label="Anything else we should know?">
                        <textarea
                          rows="5"
                          name="additional_info"
                          value={formData.additional_info}
                          onChange={handleInputChange}
                          className={`${inputCls} min-h-[120px] resize-y`}
                          placeholder="Anything you'd like us to know about you, your goals, or how you envision the partnership..."
                          data-testid="partner-additional-info"
                        />
                      </Field>
                    </div>

                    {submitError && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="partner-app-error">
                        {submitError}
                      </div>
                    )}

                    <div className="text-center pt-2">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        size="lg"
                        data-testid="partner-app-submit"
                        className="bg-gradient-to-r from-[#1E3A5F] to-[#152a45] hover:from-[#152a45] hover:to-[#0F172A] text-white px-12 py-6 rounded-xl"
                      >
                        {isSubmitting ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</>
                        ) : (
                          <>Submit Application <ArrowRight className="ml-2 h-5 w-5" /></>
                        )}
                      </Button>
                      <p className="text-sm text-gray-500 mt-4">
                        We'll respond within 1 business day · All information is confidential
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </>
    </PublicLayout>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#1E3A5F] mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 text-[#1E3A5F] placeholder:text-gray-400 bg-white";

export default PartnerApplicationPage;
